import React, { useMemo } from "react"
import type { TableModel } from "utils/tableModel"
import type { KpiTile as KpiTileDoc } from "features/Dashboard/types/dashboardDocument"
import { modelCell } from "features/Dashboard/runtime/modelAdapters"
import { useDashboardStore } from "features/Dashboard/runtime/dashboardStore"
import {
  effectiveStatistic,
  kpiBaseText,
} from "features/Dashboard/runtime/tileView"
import { TOTALS_KEY } from "constants/tableConversion"
import { formatValue } from "./chartTheme"
import { useTileTrace } from "./useTileTrace"
import TraceValue from "./TraceValue.component"

/**
 * A number tile: one cell of the table (the picked row in the picked
 * column, Totals by default), its row and column underneath and, when
 * asked, the base behind it. `decimals` overrides the cell's own places.
 * The statistic is the viewer's pick when the tile exposes one. `model` is
 * the tile's view; `rawModel`, its last result (`model` when not given),
 * gives the base when the view no longer holds it (see `kpiBaseText`).
 * A click on the number opens its trace.
 */
const KpiTile: React.FC<{
  tile: KpiTileDoc
  model: TableModel
  rawModel?: TableModel
}> = ({ tile, model, rawModel }) => {
  const controls = useDashboardStore((s) => s.tileControls[tile.id])
  const { row, column, decimals, showBase } = tile.visual
  const statistic = effectiveStatistic(tile, controls)
  const { openTrace, panel: tracePanel } = useTileTrace(tile)
  const cell = useMemo(
    () => modelCell(model, { row, column, statistic }),
    [model, row, column, statistic]
  )
  if (!cell) return <p className="dq-tile__empty">No value for this pick.</p>
  const baseText = showBase
    ? kpiBaseText(rawModel ?? model, model, { column, statistic }, cell.baseText)
    : null
  const text =
    typeof cell.raw === "number" && decimals !== undefined
      ? formatValue(cell.raw, {
          decimals,
          thousands: true,
          percent: cell.text.endsWith("%"),
        })
      : cell.text
  return (
    <div className="dq-kpi">
      <div className="dq-kpi__value" aria-label="Value">
        <TraceValue
          text={text}
          onTrace={() =>
            openTrace({ down: cell.down, across: cell.across, value: text })
          }
        />
      </div>
      <div className="dq-kpi__label">
        {cell.rowLabel}
        {cell.columnLabel !== TOTALS_KEY ? ` · ${cell.columnLabel}` : ""}
      </div>
      {baseText && <div className="dq-kpi__base">Base {baseText}</div>}
      {tracePanel}
    </div>
  )
}

export default KpiTile
